/**
 * SUPERFILTRO — la lógica pura de la barra de filtros de toda lista (reunión con
 * Josué, 14-oct-2026, punto 9: «superfiltros en las listas, por industria, estado…,
 * no botones regados»).
 *
 * Una lista declara sus FACETAS (estado, categoría, canal, responsable, fechas…);
 * la barra (`components/datos/Superfiltro.tsx`) las dibuja todas igual y los
 * valores viven en la URL con `useFiltrosEnUrl`. Aquí solo hay funciones sin
 * estado: qué chips salen, cuántos filtros hay puestos, qué cuenta cada opción,
 * si una fecha cae en el rango y cómo se dice el contador.
 */

export interface OpcionDeFaceta {
  valor: string;
  etiqueta: string;
  /** Cuántas filas caen ahí. `undefined` = no se muestra. */
  cuenta?: number;
}

/** Elige una de una lista (estado, categoría, canal, responsable…). */
export interface FacetaDeOpciones {
  tipo?: 'opciones';
  /** La clave en la URL. */
  clave: string;
  titulo: string;
  opciones: OpcionDeFaceta[];
  /** Lo que dice la opción de «sin filtro». Por defecto, «Todos». */
  todas?: string;
}

/** Un rango de fechas: dos claves en la URL, `desde` y `hasta` (AAAA-MM-DD, los dos incluidos). */
export interface FacetaDeFechas {
  tipo: 'fechas';
  /** Identifica la faceta (no va a la URL). */
  clave: string;
  titulo: string;
  desde: string;
  hasta: string;
  /** No deja elegir días que todavía no llegan (consumos, ventas hechas). */
  hastaHoy?: boolean;
}

export type Faceta = FacetaDeOpciones | FacetaDeFechas;
export type ValoresDeFacetas = Record<string, string | undefined>;

export interface ChipActivo {
  id: string;
  /** «Estado: Confirmadas». */
  texto: string;
  /** Lo que hay que escribir para quitarlo (una sola escritura en la URL). */
  quitar: ValoresDeFacetas;
}

const puesto = (v: string | undefined): v is string => v != null && v !== '';

/** Las claves de URL que maneja una faceta. */
export function clavesDe(f: Faceta): string[] {
  return f.tipo === 'fechas' ? [f.desde, f.hasta] : [f.clave];
}

/** «2026-10-05» → «05/10/2026». Lo que no es una fecha se devuelve tal cual. */
export function fechaCorta(dia: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dia);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : dia;
}

/**
 * Los chips de lo que está puesto, en el orden de las facetas. Un valor que ya no
 * está entre las opciones (un canal borrado, un enlace viejo) igual sale con su
 * valor crudo: un filtro que filtra tiene que poderse ver y quitar.
 */
export function chipsActivos(facetas: Faceta[], valores: ValoresDeFacetas): ChipActivo[] {
  const chips: ChipActivo[] = [];
  for (const f of facetas) {
    if (f.tipo === 'fechas') {
      const d = valores[f.desde];
      const h = valores[f.hasta];
      if (!puesto(d) && !puesto(h)) continue;
      const rango = puesto(d) && puesto(h)
        ? `${fechaCorta(d)} – ${fechaCorta(h)}`
        : puesto(d) ? `desde el ${fechaCorta(d)}` : `hasta el ${fechaCorta(h as string)}`;
      chips.push({ id: f.clave, texto: `${f.titulo}: ${rango}`, quitar: { [f.desde]: undefined, [f.hasta]: undefined } });
      continue;
    }
    const v = valores[f.clave];
    if (!puesto(v)) continue;
    const etiqueta = f.opciones.find((o) => o.valor === v)?.etiqueta ?? v;
    chips.push({ id: f.clave, texto: `${f.titulo}: ${etiqueta}`, quitar: { [f.clave]: undefined } });
  }
  return chips;
}

/** Cuántas facetas tienen algo puesto (el número del botón «Filtros» en el teléfono). */
export function cuantosActivos(facetas: Faceta[], valores: ValoresDeFacetas): number {
  return chipsActivos(facetas, valores).length;
}

/** Todo en blanco, en un solo objeto: «Limpiar» escribe la URL una vez, no una por filtro. */
export function sinFiltros(facetas: Faceta[], otras: string[] = ['buscar']): ValoresDeFacetas {
  return Object.fromEntries([...facetas.flatMap(clavesDe), ...otras].map((k) => [k, undefined]));
}

/**
 * Las opciones de una faceta sacadas de las filas: un valor distinto por opción,
 * con cuántas filas tiene, en orden alfabético. Los vacíos no son una opción.
 */
export function opcionesDe<T>(
  filas: readonly T[],
  valorDe: (fila: T) => string | null | undefined,
  etiquetaDe: (valor: string) => string = (v) => v,
): OpcionDeFaceta[] {
  const cuentas = new Map<string, number>();
  for (const f of filas) {
    const v = valorDe(f);
    if (puesto(v ?? undefined)) cuentas.set(v as string, (cuentas.get(v as string) ?? 0) + 1);
  }
  return [...cuentas]
    .map(([valor, cuenta]) => ({ valor, etiqueta: etiquetaDe(valor), cuenta }))
    .sort((a, b) => a.etiqueta.localeCompare(b.etiqueta, 'es'));
}

/** Pone a cada opción cuántas filas cumplen con ella. */
export function conCuentas<T>(
  opciones: OpcionDeFaceta[],
  filas: readonly T[],
  cumple: (fila: T, valor: string) => boolean,
): OpcionDeFaceta[] {
  return opciones.map((o) => ({ ...o, cuenta: filas.filter((f) => cumple(f, o.valor)).length }));
}

const dos = (n: number) => String(n).padStart(2, '0');
/** El día (AAAA-MM-DD) de un momento, en la hora de este equipo. */
export function diaLocal(momento: Date | string): string {
  const d = typeof momento === 'string' ? new Date(momento) : momento;
  return `${d.getFullYear()}-${dos(d.getMonth() + 1)}-${dos(d.getDate())}`;
}

/**
 * ¿Cae el momento entre `desde` y `hasta` (días, los dos incluidos)? Sin límites,
 * todo cae. Si vienen al revés se toman en orden: nadie pidió una lista vacía.
 */
export function enRango(momento: string | null | undefined, desde?: string, hasta?: string): boolean {
  if (!puesto(desde) && !puesto(hasta)) return true;
  if (!momento) return false;
  const dia = diaLocal(momento);
  let [a, b] = [desde, hasta];
  if (puesto(a) && puesto(b) && b < a) [a, b] = [b, a];
  return (!puesto(a) || dia >= a) && (!puesto(b) || dia <= b);
}

/** Los atajos del rango de fechas: hoy, 7 y 30 días (hoy incluido). */
export function atajosDeFecha(hoy: Date = new Date()): Array<{ etiqueta: string; desde: string; hasta: string }> {
  const hace = (dias: number) => diaLocal(new Date(hoy.getFullYear(), hoy.getMonth(), hoy.getDate() - dias));
  const h = diaLocal(hoy);
  return [
    { etiqueta: 'Hoy', desde: h, hasta: h },
    { etiqueta: 'Últimos 7 días', desde: hace(6), hasta: h },
    { etiqueta: 'Últimos 30 días', desde: hace(29), hasta: h },
  ];
}

/** «12 de 40 ventas», o «40 ventas» si no hay filtro; «1 venta» en singular. */
export function textoDeResultados(visibles: number, total: number | undefined, nombre: [string, string] = ['resultado', 'resultados']): string {
  const palabra = (n: number) => (n === 1 ? nombre[0] : nombre[1]);
  if (total == null || total === visibles) return `${visibles} ${palabra(visibles)}`;
  return `${visibles} de ${total} ${palabra(total)}`;
}
